import { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';

import NovaPanel from './components/shared/NovaPanel';
import ErrorBoundary from './components/shared/ErrorBoundary';
import NotificationDrawer from './components/shared/NotificationDrawer';
import { getNotifications } from './services/notificationService';

import UploadProductsModal from './components/modals/UploadProductsModal';
import MDApprovalModal from './components/modals/MDApprovalModal';
import BCUpdatedModal from './components/modals/BCUpdatedModal';
import BCNewModal from './components/modals/BCNewModal';
import AddProductModal from './components/modals/AddProductModal';
import NewProjectModal from './components/modals/NewProjectModal';
import DataCompletionModal from './components/modals/DataCompletionModal';

export default function AppShell() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [notifOpen, setNotifOpen] = useState(false);
  const [novaOpen, setNovaOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => {
      getNotifications()
        .then(({ unreadCount }) => { if (alive) setNotifCount(unreadCount); })
        .catch(() => {});
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(iv); };
  }, [notifOpen]);
  const [activeModal, setActiveModal] = useState(null);
  const [novaUploadFile, setNovaUploadFile] = useState(null);
  const [showNovaUpload, setShowNovaUpload] = useState(false);

  // Listen for Q2 Database Injection from Nova chat + "open Nova" from chat-history
  useEffect(() => {
    const uploadHandler = () => {
      const file = window.__novaPendingUploadFile;
      window.__novaPendingUploadFile = null;
      if (file) { setNovaUploadFile(file); setShowNovaUpload(true); }
    };
    const openNovaHandler = () => { setNovaOpen(true); };
    window.addEventListener('novaOpenUploadModal', uploadHandler);
    window.addEventListener('novaOpenPanel', openNovaHandler);
    return () => {
      window.removeEventListener('novaOpenUploadModal', uploadHandler);
      window.removeEventListener('novaOpenPanel', openNovaHandler);
    };
  }, []);

  const toggleSidebar = () => setSidebarCollapsed(c => !c);
  const toggleNotif = () => { setNotifOpen(o => !o); setNovaOpen(false); };
  const closeNotif = () => setNotifOpen(false);
  const toggleNova = () => { setNovaOpen(o => !o); setNotifOpen(false); };

  const openModal = useCallback((id) => {
    setActiveModal(id);
    setNotifOpen(false);
  }, []);

  const closeModal = useCallback(() => setActiveModal(null), []);

  return (
    <div className="app">
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

      <div className="main-area">
        <div className="topbar d-flex align-items-center gap-3">
          <Topbar
            onToggleSidebar={toggleSidebar}
            notifCount={notifCount}
            onToggleNotif={toggleNotif}
          />

        </div>

        <NotificationDrawer
          open={notifOpen}
          onClose={closeNotif}
        />

        <Outlet />
      </div>

      <ErrorBoundary fallback={null}>
        <NovaPanel open={novaOpen} onClose={toggleNova} onOpenModal={openModal} />
      </ErrorBoundary>

      <MDApprovalModal show={activeModal === 'md-approval'} onClose={closeModal} />
      <BCUpdatedModal show={activeModal === 'bc-updated'} onClose={closeModal} />
      <BCNewModal show={activeModal === 'bc-new'} onClose={closeModal} />
      <AddProductModal show={activeModal === 'add-product'} onClose={closeModal} />
      <NewProjectModal show={activeModal === 'new-project'} onClose={closeModal} />
      <DataCompletionModal show={activeModal === 'data-completion'} onClose={closeModal} />

      {showNovaUpload && (
        <UploadProductsModal
          preloadedFile={novaUploadFile}
          onClose={() => { setShowNovaUpload(false); setNovaUploadFile(null); }}
          onDone={() => { setShowNovaUpload(false); setNovaUploadFile(null); }}
        />
      )}
    </div>
  );
}
